import { Link } from "react-router-dom";
import { Plane, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { b2cPath } from "../../lib/host";

export const fmtPKR = (n) => `PKR ${n.toLocaleString()}`;

export const AirlineCard = ({ airline }) => (
  <Link
    to={b2cPath(`/${airline.slug}`)}
    data-testid={`airline-card-${airline.slug}`}
    className="group bg-white border border-slate-100 rounded-2xl p-6 hover-lift block"
  >
    <div className="flex items-center justify-between mb-4">
      <span
        className="w-11 h-11 rounded-full flex items-center justify-center font-heading font-bold text-white text-sm"
        style={{ backgroundColor: airline.brand_color || "#970C10" }}
      >
        {airline.code || <Plane className="w-5 h-5" />}
      </span>
      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#970C10] transition-colors duration-200" />
    </div>
    <h3 className="font-heading font-bold text-slate-900">{airline.name}</h3>
    <p className="text-sm text-slate-500 mt-1">{airline.tagline}</p>
    {airline.top_destinations?.[0] && (
      <p className="text-xs text-slate-400 mt-3">
        e.g. {airline.top_destinations[0].city} from{" "}
        <span className="text-[#0E7490] font-bold">{fmtPKR(airline.top_destinations[0].price_from)}</span>
      </p>
    )}
  </Link>
);

export const RouteCard = ({ route }) => {
  const daysAgo = route.last_verified_at
    ? Math.max(0, Math.floor((Date.now() - new Date(route.last_verified_at).getTime()) / 86400000))
    : null;
  return (
  <Link
    to={b2cPath(`/${route.slug}`)}
    data-testid={`route-card-${route.slug}`}
    className="group bg-white border border-slate-100 rounded-2xl p-6 hover-lift block"
  >
    <div className="flex items-center gap-3 mb-3">
      <span className="font-heading font-bold text-slate-900">{route.origin_code}</span>
      <span className="flex-1 border-t border-dashed border-slate-300 relative">
        <Plane className="w-3.5 h-3.5 text-[#0E7490] absolute left-1/2 -translate-x-1/2 -top-2 rotate-45" />
      </span>
      <span className="font-heading font-bold text-slate-900">{route.dest_code}</span>
    </div>
    <p className="text-sm font-semibold text-slate-700">
      {route.origin_city} → {route.dest_city}
    </p>
    <div className="flex items-center justify-between mt-3">
      <span className="text-xs text-slate-400">{route.duration}</span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-[8px] font-mono bg-[#0E7490]/10 text-[#0E7490] border border-[#0E7490]/25 px-1.5 py-0.5 rounded-sm uppercase tracking-widest">Net fare</span>
        <span className="text-sm font-bold text-[#0E7490]" style={{ fontVariantNumeric: "tabular-nums" }}>from {fmtPKR(route.price_from)}</span>
      </span>
    </div>
    {daysAgo !== null && (
      <p className="mt-2 text-[10px] font-mono text-slate-400 flex items-center gap-1.5" data-testid={`route-verified-${route.slug}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${daysAgo <= 7 ? "bg-green-500" : "bg-amber-400"}`} />
        fare verified {daysAgo === 0 ? "today" : daysAgo === 1 ? "yesterday" : `${daysAgo} days ago`}
      </p>
    )}
  </Link>
  );
};

export const FaqBlock = ({ faqs, testId = "faq-block", light = false }) => (
  <Accordion type="single" collapsible className="w-full" data-testid={testId} data-speakable>
    {faqs.map((f, i) => (
      <AccordionItem key={i} value={`faq-${i}`} className={light ? "border-white/10" : "border-slate-200"}>
        <AccordionTrigger className={`text-left text-sm font-semibold ${light ? "text-slate-100" : "text-slate-800"}`} data-speakable>
          {f.q}
        </AccordionTrigger>
        <AccordionContent className={`text-sm leading-relaxed ${light ? "text-slate-400" : "text-slate-500"}`} data-speakable>
          {f.a}
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const SectionHeading = ({ overline, title, subtitle, light = false }) => (
  <div className="mb-10">
    {overline && (
      <p className={`text-xs uppercase tracking-[0.2em] font-bold mb-3 ${light ? "text-[#E8767A]" : "text-[#970C10]"}`}>{overline}</p>
    )}
    <h2 className={`font-heading text-2xl sm:text-3xl font-bold tracking-tight ${light ? "text-white" : "text-slate-900"}`}>{title}</h2>
    {subtitle && <p className={`mt-3 max-w-2xl text-base ${light ? "text-slate-400" : "text-slate-500"}`}>{subtitle}</p>}
  </div>
);
